export const LEGACY_MIGRATION_STATES = [
  'inventoried',
  'mapped',
  'projected',
  'certified',
  'retired',
] as const;

export type LegacyMigrationState =
  typeof LEGACY_MIGRATION_STATES[number];

export type LegacyCommandCenterSurface = {
  id: string;
  title: string;
  sourceReference: string;
  owningSystemId: string;
  owningGovernor: string;
  category:
    | 'executive'
    | 'authority'
    | 'runtime'
    | 'simulation'
    | 'incident'
    | 'repair'
    | 'verification'
    | 'learning';
  migrationState: LegacyMigrationState;
  preserveEvidence: true;
  preserveHistory: true;
  legacyVisible: boolean;
  duplicateAuthorityAllowed: false;
  retirementBlockedUntilCertified: true;
  notes: string;
};

export const legacyCommandCenterSurfaces:
  LegacyCommandCenterSurface[] = [
    {
      id: 'legacy-sponsored-invite-pretest',
      title: 'Sponsored Invite Pretest',
      sourceReference:
        'GovernorCommandCenter.runSponsoredInvitePretest',
      owningSystemId: 'membership-billing',
      owningGovernor: 'Membership Governor',
      category: 'simulation',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Existing no-send Supabase pretest remains visible until an equivalent governed Membership projection is certified.',
    },
    {
      id: 'legacy-security-authority',
      title: 'Security Authority Evidence',
      sourceReference:
        'projectSecurityAuthorityEvidence',
      owningSystemId: 'security-auth',
      owningGovernor: 'Security Governor',
      category: 'authority',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Security authority requires its own governed workspace before retirement.',
    },
    {
      id: 'legacy-sponsored-seat-runtime',
      title: 'Sponsored Seat Runtime Evidence',
      sourceReference:
        'projectSponsoredSeatRuntimeEvidence',
      owningSystemId: 'membership-billing',
      owningGovernor: 'Membership Governor',
      category: 'runtime',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Sponsored-seat runtime evidence belongs to Membership authority.',
    },
    {
      id: 'legacy-unsponsored-family-runtime',
      title: 'Unsponsored Family Runtime Evidence',
      sourceReference:
        'projectUnsponsoredFamilyRuntimeEvidence',
      owningSystemId: 'membership-billing',
      owningGovernor: 'Membership Governor',
      category: 'runtime',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Unsponsored family access remains governed by Membership policy.',
    },
    {
      id: 'legacy-membership-counter-runtime',
      title: 'Membership Counter Runtime Evidence',
      sourceReference:
        'projectMembershipCounterRuntimeEvidence',
      owningSystemId: 'membership-billing',
      owningGovernor: 'Membership Governor',
      category: 'runtime',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Seat and invite counters belong to Membership and Billing.',
    },
    {
      id: 'legacy-canonical-route-runtime',
      title: 'Canonical Route Runtime Completion',
      sourceReference:
        'evaluateCanonicalRouteRuntimeCompletionGate',
      owningSystemId: 'routing',
      owningGovernor: 'AX Governor',
      category: 'verification',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Route authority requires a dedicated routing workspace.',
    },
    {
      id: 'legacy-calls-observations',
      title: 'Calls Runtime Observations',
      sourceReference:
        'callsRuntimeObservations',
      owningSystemId: 'calls',
      owningGovernor: 'Calls Governor',
      category: 'runtime',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Calls observations will seed the Calls governed evidence projection.',
    },
    {
      id: 'legacy-calls-incidents',
      title: 'Calls Runtime Incidents',
      sourceReference:
        'callsRuntimeIncidentState',
      owningSystemId: 'calls',
      owningGovernor: 'Calls Governor',
      category: 'incident',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Active and historical Calls incidents must remain available.',
    },
    {
      id: 'legacy-calls-response-plans',
      title: 'Calls Incident Response Plans',
      sourceReference:
        'callsIncidentResponsePlans',
      owningSystemId: 'calls',
      owningGovernor: 'Calls Governor',
      category: 'repair',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Response plans remain recommendations until registered repair authority exists.',
    },
    {
      id: 'legacy-calls-repair-authority',
      title: 'Calls Repair Authority Decisions',
      sourceReference:
        'callsRepairAuthorityDecisions',
      owningSystemId: 'calls',
      owningGovernor: 'Calls Governor',
      category: 'repair',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Calls repair authority must migrate into the shared repair orchestrator contract.',
    },
    {
      id: 'legacy-calls-pipelines',
      title: 'Calls End-to-End Pipelines',
      sourceReference:
        'callsEndToEndPipelines',
      owningSystemId: 'calls',
      owningGovernor: 'Calls Governor',
      category: 'verification',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Pipeline evidence must remain visible through Calls workspace certification.',
    },
    {
      id: 'legacy-autonomous-governance',
      title: 'Autonomous Governance',
      sourceReference:
        'runtime.autonomousRepairGovernance',
      owningSystemId: 'executive-governance',
      owningGovernor: 'Executive Governor',
      category: 'executive',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Executive governance remains an aggregate, not a domain authority.',
    },
    {
      id: 'legacy-autonomous-operations',
      title: 'Autonomous Operations',
      sourceReference:
        'runtime.autonomousOperationsOutput',
      owningSystemId: 'executive-governance',
      owningGovernor: 'Executive Governor',
      category: 'executive',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Executive operations may summarize registered system executions only.',
    },
    {
      id: 'legacy-self-healing',
      title: 'Self-Healing',
      sourceReference:
        'runtime.selfHealing',
      owningSystemId: 'repair',
      owningGovernor: 'Repair Governor',
      category: 'repair',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Self-healing must resolve through registered repair handlers.',
    },
    {
      id: 'legacy-repair-routing',
      title: 'Repair Routing',
      sourceReference:
        'runtime.repairRoutes',
      owningSystemId: 'repair',
      owningGovernor: 'Repair Governor',
      category: 'repair',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Repair routing belongs to the governed repair subsystem.',
    },
    {
      id: 'legacy-auto-repair-queue',
      title: 'Auto-Repair Queue',
      sourceReference:
        'runtime.autoRepairQueueOutput',
      owningSystemId: 'repair',
      owningGovernor: 'Repair Governor',
      category: 'repair',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Queued repairs cannot execute unless registered and approved.',
    },
    {
      id: 'legacy-verification-loop',
      title: 'Verification Loop',
      sourceReference:
        'runtime.verificationLoops',
      owningSystemId: 'verification',
      owningGovernor: 'Verification Governor',
      category: 'verification',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Verification evidence requires a dedicated governed workspace.',
    },
    {
      id: 'legacy-learning',
      title: 'Learning',
      sourceReference:
        'runtime.learningInsights',
      owningSystemId: 'learning-memory',
      owningGovernor: 'Learning Governor',
      category: 'learning',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Learning and incident memory must retain historical evidence.',
    },
    {
      id: 'legacy-executive-planning',
      title:
        'Mission, Goals, Strategy, Priority, Impact, Dashboard, and Findings',
      sourceReference:
        'GovernorCommandCenter.runtimeSignals',
      owningSystemId: 'executive-governance',
      owningGovernor: 'Executive Governor',
      category: 'executive',
      migrationState: 'mapped',
      preserveEvidence: true,
      preserveHistory: true,
      legacyVisible: true,
      duplicateAuthorityAllowed: false,
      retirementBlockedUntilCertified: true,
      notes:
        'Executive planning panels remain aggregate views of governed system evidence.',
    },
  ];

export function listLegacyCommandCenterSurfaces():
  LegacyCommandCenterSurface[] {
  return [...legacyCommandCenterSurfaces];
}

export function getLegacyCommandCenterSurface(
  id: string,
): LegacyCommandCenterSurface | null {
  return (
    legacyCommandCenterSurfaces.find(
      surface => surface.id === id,
    ) ?? null
  );
}

export function listLegacySurfacesBySystem(
  systemId: string,
): LegacyCommandCenterSurface[] {
  return legacyCommandCenterSurfaces.filter(
    surface =>
      surface.owningSystemId === systemId,
  );
}

export function validateLegacyMigrationRegistry(): {
  valid: boolean;
  duplicateIds: string[];
  unmappedIds: string[];
  retirementViolations: string[];
} {
  const counts =
    new Map<string, number>();

  for (
    const surface of
    legacyCommandCenterSurfaces
  ) {
    counts.set(
      surface.id,
      (counts.get(surface.id) ?? 0) + 1,
    );
  }

  const duplicateIds =
    [...counts.entries()]
      .filter(([, count]) => count > 1)
      .map(([id]) => id);

  const unmappedIds =
    legacyCommandCenterSurfaces
      .filter(
        surface =>
          !surface.owningSystemId ||
          !surface.owningGovernor,
      )
      .map(surface => surface.id);

  const retirementViolations =
    legacyCommandCenterSurfaces
      .filter(
        surface =>
          surface.migrationState ===
            'retired' &&
          (
            surface.legacyVisible ||
            !surface.preserveEvidence ||
            !surface.preserveHistory
          ),
      )
      .map(surface => surface.id);

  return {
    valid:
      duplicateIds.length === 0 &&
      unmappedIds.length === 0 &&
      retirementViolations.length === 0,
    duplicateIds,
    unmappedIds,
    retirementViolations,
  };
}
